import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Link2, Loader2, RefreshCw, X } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { Button } from './ui/button';

const BASE = process.env.REACT_APP_BACKEND_URL;
const field = 'w-full min-w-0 rounded-lg border border-navy/20 bg-white px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-pink-200';

export const BloomInvitePanel = ({ adminKey, refreshKey }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ advertiser_id: '', brand_label: '', ig_username: '' });
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const api = useCallback(async (path, body) => {
    const response = await fetch(`${BASE}/api/admin/bloom/${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      cache: 'no-store',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Unable to update connection links. Check your admin access.');
    return data;
  }, [adminKey]);
  const load = useCallback(async () => {
    setError('');
    try {
      const [a, b] = await Promise.all([api('invite-campaigns'), api('invites')]);
      setCampaigns(a.campaigns); setInvites(b.invitations);
    } catch (e) { setError(e.message); }
  }, [api]);
  useEffect(() => { if (adminKey) load(); }, [adminKey, refreshKey, load]);

  const create = async (event) => {
    event.preventDefault(); setBusy(true); setError(''); setCreated(null);
    try { setCreated(await api('invites', form)); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const revoke = async (id) => {
    setBusy(true); setError('');
    try {
      await api(`invites/${id}/revoke`, {});
      if (created?.id === id) setCreated(null);
      toast.success('Connection link revoked'); await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(created.url); toast.success('Connection link copied'); }
    catch { toast.error('Select and copy the link from the field.'); }
  };
  const change = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  return <section className="border-y border-navy/10 py-7 mb-8" data-testid="bloom-invite-panel">
    <Toaster richColors position="top-center" />
    <div className="flex items-center justify-between gap-3 mb-2">
      <h2 className="text-base md:text-lg font-bold text-navy flex items-center gap-2"><Link2 size={20} className="text-pink-600" /> Instagram connection links</h2>
      <Button variant="ghost" size="icon" type="button" disabled={busy || !adminKey} onClick={load} title="Refresh connection links" aria-label="Refresh connection links" data-testid="bloom-invite-refresh"><RefreshCw /></Button>
    </div>
    <p className="text-sm text-navy/65 mb-6" data-testid="bloom-invite-terms">One account per link · Expires in 48 hours · No Advecti login needed</p>
    <form onSubmit={create} className="grid sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_auto] gap-4 items-end" data-testid="bloom-invite-form">
      <label className="text-sm text-navy min-w-0">Campaign
        <select required value={form.advertiser_id} onChange={change('advertiser_id')} className={`${field} mt-1.5`} data-testid="bloom-invite-campaign">
          <option value="">Choose campaign</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.company_name || 'Unnamed campaign'} · {c.id.slice(0, 8)}</option>)}
        </select>
      </label>
      <label className="text-sm text-navy min-w-0">Brand name
        <input required maxLength={80} value={form.brand_label} onChange={change('brand_label')} placeholder="The Eagle" className={`${field} mt-1.5`} data-testid="bloom-invite-brand" />
      </label>
      <label className="text-sm text-navy min-w-0">Instagram handle
        <input required maxLength={31} pattern="@?[A-Za-z0-9._]{1,30}" value={form.ig_username} onChange={change('ig_username')} placeholder="@brandhandle" className={`${field} mt-1.5`} data-testid="bloom-invite-handle" />
      </label>
      <Button type="submit" disabled={busy || !adminKey} className="h-10 bg-pink-600 hover:bg-pink-700 text-white" data-testid="bloom-invite-create">{busy ? <Loader2 className="animate-spin" /> : <Link2 />} Create link</Button>
    </form>
    {error && <p role="alert" className="mt-4 text-sm text-red-700 break-words" data-testid="bloom-invite-error">{error}</p>}
    {created && <div className="mt-5 border-l-4 border-pink-500 pl-4" data-testid="bloom-invite-created">
      <p className="text-sm text-navy mb-2" data-testid="bloom-invite-created-summary">{created.brand_label} · @{created.ig_username} · Copy now; this private link is only shown once.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input readOnly aria-label="Private Instagram connection link" value={created.url} onFocus={(e) => e.target.select()} className={`${field} flex-1`} data-testid="bloom-invite-url" />
        <Button type="button" variant="outline" onClick={copy} data-testid="bloom-invite-copy"><Copy /> Copy link</Button>
      </div>
    </div>}
    <div className="mt-6 divide-y divide-navy/10" data-testid="bloom-invite-list">
      {!invites.length && <p className="text-sm text-navy/60 py-3" data-testid="bloom-invite-empty">No connection links yet.</p>}
      {invites.map((invite) => <div key={invite.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4" data-testid={`bloom-invite-row-${invite.id}`}>
        <div className="min-w-0 break-words">
          <p className="text-sm font-semibold text-navy" data-testid={`bloom-invite-brand-${invite.id}`}>{invite.brand_label} <span className="font-normal">· @{invite.ig_username}</span></p>
          <p className="text-xs text-navy/60 mt-1" data-testid={`bloom-invite-expiry-${invite.id}`}>{invite.company_name} · Expires {new Date(invite.expires_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`text-xs capitalize font-semibold ${invite.status === 'used' ? 'text-green-700' : 'text-navy/70'}`} data-testid={`bloom-invite-status-${invite.id}`}>{invite.status === 'used' ? 'Connected' : invite.status}</span>
          {['pending', 'connecting'].includes(invite.status) && <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => revoke(invite.id)} className="text-red-700" data-testid={`bloom-invite-revoke-${invite.id}`}><X /> Revoke</Button>}
        </div>
      </div>)}
    </div>
  </section>;
};